'use client'

import { useForm } from 'react-hook-form'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { SyneriseFormEvent, syneriseSendFormData } from 'lib/synerise'
import Button from 'shared/ui/Button'
import TextField from 'shared/ui/form/TextField'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { validateDateOfBirth } from 'shared/utils/date'
import { getApiErrors } from 'shared/utils/errors'

import { validateMaxValueDateOfBirth } from '../age'
import { useCustomerData } from '../hooks/useCustomerData'

import styles from './DateOfBirthModal.module.scss'

const defaultValues = {
  dateOfBirth: ''
}

interface DateOfBirthModalProps {
  open: boolean
  onClose: () => void
}

interface OnboardUserFormValues {
  dateOfBirth: string
}

const todayMaxDate = new Date().toISOString().split('T')[0]

const validateDateOfBirthField = (value: string) => {
  if (!value || value.trim() === '') {
    return true
  }

  if (!validateDateOfBirth(value)) {
    return 'Data urodzenia wygląda na nieprawidłową'
  }

  if (!validateMaxValueDateOfBirth(value)) {
    return 'Aby korzystać z naszych usług, musisz mieć ukończone 13 lat'
  }

  return true
}

const DateOfBirthModal = ({ open, onClose }: DateOfBirthModalProps) => {
  const {
    register,
    handleSubmit,
    trigger,
    formState: { isSubmitting, errors }
  } = useForm<OnboardUserFormValues>({
    defaultValues,
    mode: 'onChange'
  })

  const { data, update: updateSession } = useSession()
  const { update: updateCustomer } = useCustomerData()

  const onSubmit = async (values: OnboardUserFormValues) => {
    await updateCustomer({ dateOfBirth: values.dateOfBirth })
      .then(async updated => {
        if (data) {
          await updateSession({
            ...data,
            user: {
              ...data.user,
              customer: { ...data.user.customer, ...updated }
            }
          })
        }

        onClose()
        syneriseSendFormData({
          formData: {
            birthdate: values.dateOfBirth
          },
          fieldMapping: {
            birthdate: 'birthdate',
            firstname: 'firstName',
            lastname: 'lastName'
          },
          event: SyneriseFormEvent.PROFILE_EDIT
        })
        toast.success('Pomyślnie zapisano zmiany')
      })
      .catch(err => {
        reportSentryError(err, 'Failed to update user details')
        toast.error(getApiErrors(err))
      })
  }

  const handleBlur = () => {
    void trigger('dateOfBirth')
  }

  return (
    <Modal id='date-of-birth-modal' open={open} onClose={onClose}>
      <ModalTitle>Podaj datę urodzenia</ModalTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.inputWrapper}>
          <TextField
            id='dob_modal_date_input'
            required
            label='Data urodzenia'
            type='date'
            error={Boolean(errors.dateOfBirth)}
            helperText={errors.dateOfBirth?.message}
            inputProps={{
              max: todayMaxDate,
              required: false
            }}
            {...register('dateOfBirth', {
              required: 'Data jest wymagana',
              validate: validateDateOfBirthField
            })}
            onBlur={handleBlur}
          />
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            fullWidth
            disabled={isSubmitting || Boolean(errors.dateOfBirth)}
            type='submit'
          >
            Potwierdź
          </Button>
          <Button
            fullWidth
            variant='neutralOutline'
            onClick={onClose}
            type='button'
          >
            Anuluj
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default DateOfBirthModal
